/**
 * Polling tabanlı onay akışı — socket.io'nun yerini alır.
 *
 * Eskiden backend her onay için socket.demapi.de'de bir "room" açıyor, burası
 * o odayı dinliyordu. Socket tek makineydi; 502 verdiğinde backend `createRoom()`
 * false alıp 500 dönüyor ve pin satışı / bakiye / kredi onayı komple duruyordu.
 *
 * Yeni akış: backend `poll_token` döner, biz `GET auth/approval-status/{token}`
 * ucunu yokluyoruz.
 *
 *   202 → hâlâ bekliyor. Cevaptaki YENİ poll_token ile devam edilmeli (token
 *         her yoklamada rotate ediliyor — replay koruması).
 *   200 → onaylandı, `code` gelir; asıl istek `ConfirmationCode` header'ıyla
 *         tekrar gönderilir (socket akışındaki `msg.code` ile aynı rol).
 *   403 → reddedildi ya da IP/ClientId eşleşmedi
 *   410 → süre doldu
 */

export type ApprovalData = {
  poll_token: string;
  expires_at?: number;
  type?: string;
};

export type ApprovalOutcome = {
  status: boolean;
  code?: string;
  reason?: "rejected" | "fingerprint_mismatch" | "expired" | "not_found";
};

/** Onay isteğinin toplam ömrü. Backend TTL'i de 180sn. */
export const APPROVAL_TIMEOUT = 180 * 1000;

/** İki yoklama arası bekleme. */
export const APPROVAL_POLL_INTERVAL = 2000;

/**
 * Yoklamayı başlatır. Dönen fonksiyon çağrıldığında yoklama durur
 * (modal kapatma / unmount cleanup için).
 *
 * `get` olarak `useApi(true).$get` geçilmeli — noAlert=true, aksi hâlde her
 * 403/410 için kullanıcıya toast basılır.
 */
export function startApprovalPolling(get: (url: string) => Promise<any>, approval: ApprovalData, onOutcome: (outcome: ApprovalOutcome) => void): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let token = approval.poll_token;
  const deadline = Date.now() + APPROVAL_TIMEOUT;

  const finish = (outcome: ApprovalOutcome) => {
    if (cancelled) return;
    cancelled = true;
    if (timer) clearTimeout(timer);
    onOutcome(outcome);
  };

  const schedule = () => {
    timer = setTimeout(tick, APPROVAL_POLL_INTERVAL);
  };

  const tick = async () => {
    if (cancelled) return;

    if (Date.now() > deadline) {
      finish({ status: false, reason: "expired" });
      return;
    }

    try {
      const res = await get(`auth/approval-status/${token}`);
      if (cancelled) return;

      if (res?.approval === "approved") {
        finish({ status: true, code: res.code });
        return;
      }

      // 202 — rotate edilen token ile devam
      if (res?.poll_token) token = res.poll_token;
      schedule();
    } catch (err: any) {
      if (cancelled) return;

      const reason = err?.reason ?? err?.data?.reason;
      const httpCode = err?.code ?? err?.response?.status;

      if (reason === "rejected" || reason === "fingerprint_mismatch" || httpCode === 403) {
        finish({ status: false, reason: reason ?? "rejected" });
        return;
      }
      if (reason === "expired" || httpCode === 410) {
        finish({ status: false, reason: "expired" });
        return;
      }
      if (reason === "not_found" || httpCode === 404) {
        finish({ status: false, reason: "not_found" });
        return;
      }

      // Geçici ağ/sunucu hatası — onay isteği hâlâ geçerli, yoklamaya devam.
      // Deadline dolunca yukarıdaki kontrol zaten sonlandırır.
      schedule();
    }
  };

  schedule();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}
